class CartBar extends HTMLElement {
  constructor() {
    super();
    this.boundHandleClick = this.handleClick.bind(this);
    this.boundResetATC = this.resetATC.bind(this);
    this.boundOptionChange = this.handleOptionChange.bind(this);
    this.boundSyncSpace = this.syncSpace.bind(this);
  }

  connectedCallback() {
    this.section = this.closest('product-info');
    this.form = this.section?.querySelector('[data-product-form]');
    if (!this.section || !this.form) return;

    this.formWrapper = (window.theme?.settings?.productPageSticky && this.section.querySelector('[data-form-wrapper]')) || this.form;
    this.image = this.querySelector('[data-cart-bar-image]');
    this.options = this.querySelector('[data-cart-bar-options]');
    this.qty = this.querySelector('[data-cart-bar-qty]');
    this.atcBtn = this.querySelector('[data-cart-bar-add-to-cart]');
    this.atcText = this.querySelector('[data-add-to-cart-text]');
    this.barWrapper = this.querySelector('[data-form-wrapper]');
    this.preorderHolder = this.form.querySelector('[id^="PreorderProperty-"]');

    this.addEventListener('click', this.boundHandleClick);
    ['theme:cart:refresh', 'theme:cart-bar:reset-atc-animation'].forEach((e) => document.addEventListener(e, this.boundResetATC));

    this.initOptions();
    this.initQty();
    this.subVariant();
    this.initObserver();
    this.initSpace();
  }

  initSpace() {
    this.syncSpace();

    if ('ResizeObserver' in window) {
      this.resizeObs = new ResizeObserver(this.boundSyncSpace);
      this.resizeObs.observe(this);
    } else {
      window.addEventListener('resize', this.boundSyncSpace);
    }
  }

  syncSpace() {
    if (!this.classList.contains('is-visible')) {
      this.clearSpace();
      return;
    }

    const height = this.offsetHeight;
    document.documentElement.style.setProperty('--cart-bar-height', `${height}px`);
  }

  setVisible(visible) {
    this.classList.toggle('is-visible', visible);
    this.syncSpace();
  }

  clearSpace() {
    document.documentElement.style.removeProperty('--cart-bar-height');
  }

  resetATC() {
    this.atcBtn?.classList.remove('is-loading');
    const locked = this.barWrapper?.matches('.variant--soldout, .variant--unavailable');
    if (!locked) this.atcBtn?.removeAttribute('disabled');
  }

  handleClick(e) {
    if (e.target.closest('[data-scroll-to-form]')) {
      e.preventDefault();
      const target = this.section.querySelector('variant-selects') || this.formWrapper;
      target.setAttribute('tabindex', '-1');
      target.focus({preventScroll: true});
      window.theme.scrollTo(target.getBoundingClientRect().top);
      return;
    }

    if (e.target.closest('[data-cart-bar-add-to-cart]')) {
      e.preventDefault();
      const mainBtn = this.form.querySelector('[data-add-to-cart]');
      if (!mainBtn || mainBtn.hasAttribute('disabled')) return;

      this.atcBtn.classList.add('is-loading');
      this.atcBtn.setAttribute('disabled', true);

      if (this.form.requestSubmit) this.form.requestSubmit(mainBtn);
      else mainBtn.click();
    }
  }

  initOptions() {
    this.options?.querySelectorAll('[data-popout-input]').forEach((input) => input.addEventListener('change', this.boundOptionChange));
  }

  handleOptionChange(e) {
    const id = e.target.getAttribute('data-option-value-id');
    if (!id) return;

    const mainVariantSelects = this.section?.querySelector('variant-selects');
    const target = mainVariantSelects?.querySelector(`[data-option-value-id="${CSS.escape(id)}"]`);
    if (!target) return;

    const event = new MouseEvent('click', {bubbles: true, cancelable: true});
    event.fromCartBar = true;

    if (!target.matches('a, input')) {
      (target.querySelector('.select-popout__option') || target.closest('label, a, [role="button"]'))?.dispatchEvent(event);
      return;
    }

    target.dispatchEvent(event);
    if (target.matches('input')) {
      const change = new Event('change', {bubbles: true});
      change.fromCartBar = true;
      target.dispatchEvent(change);
    }
  }

  subVariant() {
    if (!window.subscribe || !window.theme?.PUB_SUB_EVENTS?.variantChange) return;
    this.unsub = window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, ({data}) => {
      const sid = data?.sectionId;
      if (!sid || (sid !== this.section.dataset.section && sid !== this.section.dataset.originalSection)) return;

      this.updateImage(data.variant);
      this.updateOptions(data.variant);
      this.updateBtn(data.variant);
      this.updatePrice(data.variant);
    });
  }

  updateImage(variant) {
    const img = this.image?.querySelector('img');
    const src = variant?.featured_media?.preview_image?.src;
    if (img && src) {
      img.src = `${src}${src.includes('?') ? '&' : '?'}width=160`;
    }
  }

  updateOptions(variant) {
    if (!this.options || !variant?.options) return;

    const mainVariantSelects = this.section?.querySelector('variant-selects');
    if (!mainVariantSelects) return;

    this.options.querySelectorAll('popout-select').forEach((select) => {
      const input = select.querySelector('[data-popout-input]');
      const idx = parseInt(input?.dataset.index?.replace('option', '') || '', 10);
      if (!input || isNaN(idx)) return;

      const currentVal = variant.options[idx - 1];
      const popoutText = select.querySelector('[data-popout-text]');

      select.querySelectorAll('[data-popout-option]').forEach((opt) => {
        const val = opt.dataset.value;
        const isSelected = val === currentVal;
        const item = opt.closest('li');

        if (isSelected && item) {
          if (popoutText) popoutText.textContent = val;
          input.value = val;

          if (item.dataset.optionValueId) input.setAttribute('data-option-value-id', item.dataset.optionValueId);
          if (item.dataset.productUrl) input.setAttribute('data-product-url', item.dataset.productUrl);

          select.querySelector('.select-popout__item--current')?.classList.remove('select-popout__item--current');
          item.classList.add('select-popout__item--current');
          select.querySelector('[aria-current="true"]')?.removeAttribute('aria-current');
          opt.setAttribute('aria-current', 'true');
        }

        const realId = item?.dataset.optionValueId;
        if (realId) {
          const realOpt = mainVariantSelects.querySelector(`[data-option-value-id="${CSS.escape(realId)}"]`);
          if (realOpt) {
            const isUnavailable = realOpt.closest('.sold-out, .unavailable') || realOpt.disabled || realOpt.querySelector('.sold-out, .unavailable');
            opt.classList.toggle('unavailable', !!isUnavailable);
          }
        }
      });
    });
  }

  updateBtn(variant) {
    const btn = this.atcBtn;
    const txt = this.atcText;
    if (!btn || !txt) return;

    const unavailable = !variant;
    const soldOut = Boolean(variant && !variant.available);
    const strings = window.theme?.strings || {};

    this.barWrapper?.classList.toggle('variant--unavailable', unavailable);
    this.barWrapper?.classList.toggle('variant--soldout', soldOut);

    let label = strings.add_to_cart || 'Add to cart';
    if (unavailable) label = strings.unavailable || 'Unavailable';
    else if (soldOut) label = strings.sold_out || 'Sold out';
    else if (this.preorderHolder?.querySelector('input')) label = strings.preorder || 'Pre-order';

    txt.textContent = label;
    btn.setAttribute('aria-label', label);
    btn.toggleAttribute('disabled', unavailable || soldOut);
  }

  updatePrice(variant) {
    const price = this.querySelector('[data-product-price]');
    if (!price || !variant) return;

    const fmt = (v) => window.theme.formatMoney(v, window.theme?.settings?.currency_code_enable ? window.theme.moneyWithCurrencyFormat : window.theme.moneyFormat);

    price.innerHTML = variant.price === 0 ? window.theme?.strings?.free || 'Free' : fmt(variant.price);

    const onSale = variant.compare_at_price > variant.price;
    price.classList.toggle('product__price--sale', onSale);

    const compare = this.querySelector('.product__price--strike');
    if (compare) {
      compare.textContent = onSale ? fmt(variant.compare_at_price) : '';
      compare.classList.toggle('hidden', !onSale);
    }
  }

  initQty() {
    if (!this.qty) return;
    const barQty = this.qty.querySelector('[data-quantity-field]');
    const mainQty = Array.from(this.section.querySelectorAll('[data-quantity-holder]'))
      .find((el) => !this.contains(el))
      ?.querySelector('[data-quantity-field]');

    if (!barQty || !mainQty) return;

    barQty.value = mainQty.value;
    let syncing = false;

    const sync = (src, dest) => {
      if (syncing) return;
      syncing = true;
      dest.value = src.value;
      dest.dispatchEvent(new Event('change', {bubbles: true}));
      syncing = false;
    };

    this.syncMain = () => sync(mainQty, barQty);
    this.syncBar = () => sync(barQty, mainQty);

    [mainQty, barQty].forEach((el) => ['input', 'change'].forEach((e) => el.addEventListener(e, el === mainQty ? this.syncMain : this.syncBar)));
    this.mainQtyRef = mainQty;
    this.barQtyRef = barQty;
  }

  initObserver() {
    this.obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => this.setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0));
    });
    this.obs.observe(this.formWrapper);
  }

  disconnectedCallback() {
    ['theme:cart:refresh', 'theme:cart-bar:reset-atc-animation'].forEach((e) => document.removeEventListener(e, this.boundResetATC));
    this.obs?.disconnect();

    if (this.resizeObs) this.resizeObs.disconnect();
    else window.removeEventListener('resize', this.boundSyncSpace);
    this.clearSpace();

    this.unsub?.();
    this.removeEventListener('click', this.boundHandleClick);

    this.options?.querySelectorAll('[data-popout-input]').forEach((i) => i.removeEventListener('change', this.boundOptionChange));

    if (this.mainQtyRef) ['input', 'change'].forEach((e) => this.mainQtyRef.removeEventListener(e, this.syncMain));
    if (this.barQtyRef) ['input', 'change'].forEach((e) => this.barQtyRef.removeEventListener(e, this.syncBar));
  }
}

if (!customElements.get('cart-bar')) customElements.define('cart-bar', CartBar);
